import { useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, FileText, Upload, Video } from 'lucide-react';
import { AdminPageHeader } from '../../layouts/admin-page-header.js';
import { AdminContent } from '../../layouts/admin-content.js';
import { AdminSectionCard } from '../../components/primitives/admin-section-card.js';
import { AdminFilterBar } from '../../components/primitives/admin-filter-bar.js';
import { AdminSearchInput } from '../../components/primitives/admin-search-input.js';
import { AdminSelect } from '../../components/primitives/admin-select.js';
import { AdminRefreshButton } from '../../components/primitives/admin-refresh-button.js';
import { AdminDataTable, type AdminDataTableColumn } from '../../components/primitives/admin-data-table.js';
import { AdminStatusBadge } from '../../components/primitives/admin-status-badge.js';
import { useDebouncedValue } from '../../lib/use-debounced-value.js';
import { formatDate } from '../../lib/format.js';
import { useAdminMediaLibrary, useUploadMedia } from '../hooks/use-media.js';
import { AdminApiError } from '../../api/admin-client.js';
import type { AdminMediaAsset } from '../types/media.js';

const PAGE_SIZE = 30;

function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function MediaPreview({ row }: { row: AdminMediaAsset }) {
  if (row.kind === 'image') {
    return row.url ? (
      <img
        src={row.url}
        alt=""
        className="h-12 w-12 rounded-lg border border-gray-100 object-cover"
        loading="lazy"
      />
    ) : (
      <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-gray-100 bg-gray-50 text-[10px] text-gray-400">
        No URL
      </div>
    );
  }
  if (row.kind === 'video') {
    return row.url ? (
      <video src={row.url} muted className="h-12 w-12 rounded-lg border border-gray-100 object-cover" />
    ) : (
      <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-gray-100 bg-gray-50 text-gray-400">
        <Video className="h-5 w-5" aria-hidden="true" />
      </div>
    );
  }
  return (
    <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-gray-100 bg-gray-50 text-gray-400">
      <FileText className="h-5 w-5" aria-hidden="true" />
    </div>
  );
}

/**
 * Browses the media library (both the existing Drive-backed assets and any
 * newly uploaded R2-backed ones -- see docs/media-r2-storage.md) and
 * supports uploading new image/video/PDF files directly. The Drive file id
 * is never fetched or rendered, only the derived public url/dimensions/mime
 * already exposed by AdminMediaAssetDto.
 */
export function MediaLibraryPage() {
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);
  const [kind, setKind] = useState('');
  const [orphanedOnly, setOrphanedOnly] = useState('');
  const [page, setPage] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadMedia();

  const params = useMemo(
    () => ({
      search: search || undefined,
      kind: (kind || undefined) as 'image' | 'document' | 'video' | undefined,
      orphanedOnly: orphanedOnly === 'true' ? true : undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [search, kind, orphanedOnly, page],
  );

  const { data, isLoading, isError, refetch, isFetching } = useAdminMediaLibrary(params);
  const rows = data ?? [];

  function handleFileSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploadError(null);
    upload.mutate(file, {
      onError: (error) =>
        setUploadError(error instanceof AdminApiError ? error.message : 'Could not upload file.'),
    });
  }

  const columns: AdminDataTableColumn<AdminMediaAsset>[] = [
    {
      key: 'preview',
      header: 'Preview',
      render: (row) => <MediaPreview row={row} />,
    },
    { key: 'mimeType', header: 'Type', render: (row) => row.mimeType },
    {
      key: 'dimensions',
      header: 'Dimensions',
      render: (row) => (row.width && row.height ? `${row.width}×${row.height}` : '—'),
    },
    { key: 'sizeBytes', header: 'Size', render: (row) => formatBytes(row.sizeBytes) },
    { key: 'linkCount', header: 'Linked to', render: (row) => `${row.linkCount} product${row.linkCount === 1 ? '' : 's'}` },
    {
      key: 'orphaned',
      header: 'Status',
      render: (row) =>
        row.orphaned ? (
          <AdminStatusBadge label="Orphaned" tone="warning" />
        ) : (
          <AdminStatusBadge label="In use" tone="success" />
        ),
    },
    { key: 'createdAt', header: 'Added', render: (row) => formatDate(row.createdAt) },
  ];

  return (
    <AdminContent>
      <AdminPageHeader
        title="Media library"
        description="Browse and upload media assets. Link images or videos to products from the product editor's Media tab."
        breadcrumbs={[{ label: 'Media' }]}
        actions={
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,video/mp4,application/pdf"
              className="hidden"
              onChange={handleFileSelected}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={upload.isPending}
              className="bg-brand text-brand-foreground focus-visible:ring-brand inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 disabled:opacity-60"
            >
              <Upload className="h-4 w-4" aria-hidden="true" />
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </button>
            <AdminRefreshButton onRefresh={() => void refetch()} isRefreshing={isFetching} />
          </div>
        }
      />

      {uploadError && (
        <p className="mb-3 text-sm text-red-600" role="alert">
          {uploadError}
        </p>
      )}

      <AdminSectionCard title="All assets" className="p-4 sm:p-5">
        <AdminFilterBar>
          <AdminSearchInput
            value={searchInput}
            onChange={(value) => {
              setSearchInput(value);
              setPage(0);
            }}
            placeholder="Search media…"
            label="Search media"
          />
          <AdminSelect
            label="Kind"
            value={kind}
            onChange={(value) => {
              setKind(value);
              setPage(0);
            }}
            allLabel="All kinds"
            options={[
              { value: 'image', label: 'Image' },
              { value: 'video', label: 'Video' },
              { value: 'document', label: 'Document' },
            ]}
          />
          <AdminSelect
            label="Link status"
            value={orphanedOnly}
            onChange={(value) => {
              setOrphanedOnly(value);
              setPage(0);
            }}
            allLabel="All assets"
            options={[{ value: 'true', label: 'Orphaned only' }]}
          />
        </AdminFilterBar>

        <AdminDataTable
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          emptyTitle="No media assets found"
        />

        <nav aria-label="Pagination" className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="focus-visible:ring-brand flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Previous
          </button>
          <span>Page {page + 1}</span>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={rows.length < PAGE_SIZE}
            className="focus-visible:ring-brand flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 disabled:opacity-40"
          >
            Next
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </nav>
      </AdminSectionCard>
    </AdminContent>
  );
}
